import { Timeline, permissionAnswer, type Block, type Raw } from '@one-deployment/claude-stream'

/**
 * A headless session's chat (docs/features/26-live-chat.md): the box sends
 * the wrapper's stream-json lines as they are written, and the dashboard
 * folds them into one Timeline. `reset` starts over (a new connection, or the
 * session switched from its terminal view to the stream).
 */
export function feed(tl: Timeline | null, frame: { lines: string[]; reset: boolean }): Timeline {
  const t = frame.reset || !tl ? new Timeline() : tl
  for (const l of frame.lines) {
    let o: Raw
    // A torn or foreign line is kept visible as one, never dropped.
    try { o = JSON.parse(l) } catch { o = { type: 'trisof', subtype: 'bad_line', line: l } }
    t.push(o)
  }
  return t
}

export type Answer = { allow: true; input?: Raw } | { allow: false; message?: string }

/** The control message that answers a permission card; Allow keeps the input Claude asked to run. */
export function answerOf(p: Extract<Block, { kind: 'permission' }>, a: Answer) {
  return a.allow
    ? permissionAnswer(p.id, { allow: true, input: a.input ?? p.input })
    : permissionAnswer(p.id, { allow: false, message: a.message || 'The person watching declined this.' })
}

/** Claude is mid-turn: anything but idle, waiting on nobody, or gone. */

/** The footer's line: model, context used, cost so far. */
export function footer(t: Timeline): string {
  const cost = t.blocks.reduce((s, b) => s + (b.kind === 'result' && b.costUsd ? b.costUsd : 0), 0)
  return [
    t.session.model,
    t.contextTokens ? `${Math.round(t.contextTokens / 1000)}k context` : null,
    cost ? `$${cost.toFixed(cost < 1 ? 3 : 2)}` : null,
    t.rateLimit && t.rateLimit.status !== 'allowed' ? `limit ${t.rateLimit.status}` : null,
  ].filter(Boolean).join(' · ')
}

/** Context Claude can hold before it compacts the conversation by itself (about this share of the window). */
const AUTO_COMPACT_AT = 0.92
const FINISHED = new Set(['completed', 'failed', 'stopped', 'killed', 'cancelled', 'error'])

/**
 * What the terminal's bottom lines show, from the stream (ONE-9): background
 * shells, subagents still working and what each is on, and how far the
 * conversation is from compacting itself.
 */
/** The box's `status` frame (ONE-9): the branch, and in terminal mode the TUI footer. */
export type BoxStatus = { branch?: string | null; permission_mode?: string | null; shells?: number | null; agents_waiting?: number | null; compact_pct?: number | null; subagents: string[] }
export type LiveStatusData = { branch: string | null; shells: number; waiting: number; agents: { id: string; text: string }[]; untilCompact: number | null }

/**
 * What the terminal's bottom lines say, for the chat. A headless session's
 * comes from its stream; a terminal one's from the TUI footer the box reads.
 */
export function liveStatus(t: Timeline | null, box?: BoxStatus | null): LiveStatusData {
  const branch = box?.branch ?? null
  if (!t) {
    const agents = (box?.subagents ?? []).map((text, i) => ({ id: String(i), text }))
    return { branch, shells: box?.shells ?? 0, waiting: box?.agents_waiting ?? agents.length, agents, untilCompact: box?.compact_pct ?? null }
  }
  const running = [...t.tasks.values()].filter((k) => !FINISHED.has(k.status))
  const window = /\[1m\]/i.test(t.session.model ?? '') ? 1_000_000 : 200_000
  const agents = running.filter((k) => k.type !== 'local_bash').map((k) => ({ id: k.id, text: [k.description || 'Agent', k.lastTool].filter(Boolean).join(' · ') }))
  return {
    branch,
    shells: running.filter((k) => k.type === 'local_bash').length,
    waiting: agents.length,
    agents,
    untilCompact: t.contextTokens ? Math.max(0, Math.round(100 * (1 - t.contextTokens / (window * AUTO_COMPACT_AT)))) : null,
  }
}

/** One kind of line the chat could not read, as reported to the Worker: names only, never content. */
export type UnknownLine = { kind: string; keys: string[]; unreadable: boolean }

const NAME = /^[\w./:-]{1,64}$/
const name = (s: string) => s.replace(/[^\w./:-]+/g, '_').slice(0, 64) || '_'

/**
 * The unknown blocks in a timeline, one per kind, skipping kinds in `seen`
 * (and adding them to it), so each kind is reported once per session.
 */
export function newUnknowns(t: Timeline, seen: Set<string>): UnknownLine[] {
  const out: UnknownLine[] = []
  const walk = (bs: Block[]) => {
    for (const b of bs) {
      if (b.kind === 'tool') walk(b.children)
      if (b.kind !== 'unknown') continue
      const unreadable = b.label.includes('(unreadable:')
      // The label's head is the line kind (labelOf, or the block it carried); the rest may quote an error.
      const kind = name(b.label.replace(/ \(.*$/s, ''))
      const key = `${kind}|${unreadable}`
      if (seen.has(key)) continue
      seen.add(key)
      const raw = b.raw && typeof b.raw === 'object' ? b.raw : {}
      out.push({ kind, keys: Object.keys(raw).filter((k) => NAME.test(k)).slice(0, 40), unreadable })
    }
  }
  walk(t.blocks)
  return out.slice(0, 20)
}
